import { api } from './api';

export interface Driver {
  name: string;
  basis: 'history_avg' | 'history_trend' | 'user_input';
  value: number;
  rationale: string;
  flagged?: boolean;
}

export interface Forecast {
  id: string;
  workspaceId: string;
  scenarioLabel: string;
  drivers: Driver[];
  aiSuggestions: Driver[] | null;
  createdBy: string | null;
  createdAt: string;
  updatedAt: string;
  scenarioKey?: string;
  horizonStart?: string | null;
  horizonEnd?: string | null;
  baseScenario?: string;
  lastComputedAt?: string | null;
  rollingEnabled?: boolean;
  rollingHorizonMonths?: number | null;
}

export interface ScenarioInfo {
  scenario: string;
  rows: number;
  periodStart: string | null;
  periodEnd: string | null;
}

export interface ComputeResult {
  scenario: string;
  baseScenario: string;
  rowsWritten: number;
  accounts: number;
  periods: number;
  totalsByPeriod: Record<string, number>;
}

export interface CompareRow {
  accountCode: string;
  currency: string | null;
  a: number;
  b: number;
  delta: number;
  pct: number | null;
}

export interface CompareResult {
  scenarioA: string;
  scenarioB: string;
  period: { start: string; end: string };
  rows: CompareRow[];
  totals: { a: number; b: number; delta: number };
}

export interface RollForwardResult {
  scenario: string;
  baseScenario: string;
  lockedThrough: string;
  lockedPeriods: number;
  projectedPeriods: number;
  rowsWritten: number;
  totalsByPeriod: Record<string, number>;
}

export const forecastsApi = {
  async list(workspaceId: string): Promise<Forecast[]> {
    const r = await api.get<Forecast[]>(`/api/workspaces/${workspaceId}/forecasts`);
    return r.data;
  },
  async create(workspaceId: string, body: { scenarioLabel: string; drivers: Driver[]; aiSuggestions?: Driver[] }): Promise<Forecast> {
    const r = await api.post<Forecast>(`/api/workspaces/${workspaceId}/forecasts`, body);
    return r.data;
  },
  async update(id: string, drivers: Driver[]): Promise<Forecast> {
    const r = await api.patch<Forecast>(`/api/forecasts/${id}`, { drivers });
    return r.data;
  },
  /** Delete a forecast scenario. Projected facts already written are kept. */
  async remove(id: string): Promise<void> {
    await api.delete(`/api/forecasts/${id}`);
  },
  async proposeDrivers(workspaceId: string, body: { periodStart: string; periodEnd: string; contextNote?: string }): Promise<{ suggestions: Driver[]; costUsd: number; raw: string; citedFactIds: string[] }> {
    const r = await api.post(`/api/workspaces/${workspaceId}/forecasts/propose-drivers`, body);
    return r.data;
  },
  async scenarios(workspaceId: string): Promise<ScenarioInfo[]> {
    const r = await api.get<ScenarioInfo[]>(`/api/workspaces/${workspaceId}/forecasts/scenarios`);
    return r.data;
  },
  async compute(id: string, body: { horizonStart: string; horizonEnd: string; baseScenario?: string }): Promise<ComputeResult> {
    const r = await api.post<ComputeResult>(`/api/forecasts/${id}/compute`, body);
    return r.data;
  },
  async compare(workspaceId: string, body: { scenarioA: string; scenarioB: string; periodStart?: string; periodEnd?: string }): Promise<CompareResult> {
    const r = await api.post<CompareResult>(`/api/workspaces/${workspaceId}/forecasts/compare`, body);
    return r.data;
  },
  async rollForward(id: string, body: { throughPeriod: string }): Promise<RollForwardResult> {
    const r = await api.post<RollForwardResult>(`/api/forecasts/${id}/roll-forward`, body);
    return r.data;
  },
  async simulate(id: string, body: { horizonStart: string; horizonEnd: string; drivers?: Driver[]; baseScenario?: string }): Promise<SimulateResult> {
    const r = await api.post<SimulateResult>(`/api/forecasts/${id}/simulate`, body);
    return r.data;
  },
  async setRolling(id: string, body: { enabled: boolean; horizonMonths?: number }): Promise<Forecast> {
    const r = await api.patch<Forecast>(`/api/forecasts/${id}/rolling`, body);
    return r.data;
  },
};

export interface SimulateResult {
  periods: string[];
  baseline: number[];
  scenario: number[];
  /** P10 / P90 confidence bands around the scenario (central) projection. */
  p10: number[];
  p90: number[];
  /** Historical volatility (σ of month-over-month % change) used for the bands. */
  sigma: number;
  baseScenario: string;
}
